import styled from "styled-components";
import { useCallback, useEffect, useState } from "react";
import { nvrToolModes, uploadTypes } from "../../lib/types";
import { useDropzone } from "react-dropzone";
import colours from "../../lib/colours";
import { ReactComponent as TickIcon } from "../../assets/svg/tick.svg";
import Callout from "../../components/molecules/Callout";
import FilledButton, {
  FilledButtonAlt,
} from "../../components/atoms/FilledButton";
import { IState } from "../../context/featuresReducer";
import {
  useFeaturesContext,
  useFeaturesDispatchContext,
  useMapContext,
  useMiscellaneousContext,
  useNvrToolModeContext,
  useShpFileNamesContext,
  useUploadTypeContext,
} from "../../context/ContextProvider";
import HelpButton from "../../components/atoms/HelpButton";
import { IProps as DialogProps } from "../../components/organisms/Dialog";
import { useDialogContext } from "../../context/ContextProvider";
import { initialDialogProps } from "../Other/DialogContainer";
import {
  UploadResults,
  checkNoAdminFields,
  getDetailedUploadResults,
  getSimpleUploadResults,
  readAndConvertShapefile,
  validateZipFileContents,
} from "../../utils/uploadUtils";
import ProgressMessage from "../../components/atoms/ProgressMessage";
import HowToUpload from "./HowToUpload";
import DialogCallout from "../../components/other/DialogCallout";
import Dropdown from "../../components/atoms/Dropdown";
import { Link as LinkComponent } from "../../components/atoms/Link";
import { nvrEmailAddress } from "../../lib/constants";
import { useAuthenticator } from "@aws-amplify/ui-react";

const Container = styled.div`
  overflow: auto;
`;

const UploadTypeContainer = styled.div`
  padding-inline: 12px;
  padding-block: 14px;
  margin-top: 4px;
`;

const DropzoneContainer = styled.div`
  padding: 8px 12px;
`;

const StyledDropZone = styled.div`
  border: 3px dashed ${colours.th2};
  color: ${colours.th1};
  transition: color 0.2s;
  padding: 20px 12px;
  text-align: center;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    color: ${colours.th1l2};
  }
`;

const HelpButtonContainer = styled.span`
  display: inline-flex;
  margin-left: 8px;
`;

const FileListContainer = styled.div`
  padding: 2px 12px;
`;

const FileContainer = styled.div`
  font-size: 14px;
  font-weight: 300px;
  color: ${colours.th1};
  margin-block: 2px;
`;

const FileErrorContainer = styled.div`
  padding: 4px 12px;
`;

const ActionsContainer = styled.div`
  padding: 8px 12px;
  display: flex;
  flex-direction: row-reverse;
`;

const ImportErrorContainer = styled.div`
  margin-top: 12px;
`;

const SimpleUploadIssuesList = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

const DetailedUploadIssuesList = styled.ol`
  margin: 0;
  padding-left: 20px;
`;

const DetailedUploadIssuesListItem = styled.li`
  white-space: pre-line;
  margin-block: 12px;
`;

const MessageContainer = styled.div`
  padding: 12px;
  padding-top: 24px;
`;

const tickStyle = {
  height: "9px",
  marginLeft: "-2px",
  marginRight: "1px",
  color: colours.th2d1,
};

const acceptedFileTypes = {
  "application/x-zip-compressed": [".zip"],
  "application/zip": [".zip"],
};

function UploadWidget() {
  const { user } = useAuthenticator((context) => [context.user]);
  const { nvrToolMode }: { nvrToolMode: nvrToolModes } =
    useNvrToolModeContext();
  const { uploadType, setUploadType, uploadTypeFixed } = useUploadTypeContext();
  const state: IState = useFeaturesContext();
  const dispatch = useFeaturesDispatchContext();
  const { map } = useMapContext();
  const [zipFiles, setZipFiles] = useState<any[]>([]);
  const { setShpFileNames } = useShpFileNamesContext();
  const [fileTypeError, setFileTypeError] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<any>(undefined);
  const [existingFeatures, setExistingFeatures] = useState(false);
  const [existingFeaturesWarning, setExistingFeaturesWarning] = useState(false);
  const { setDialogProps }: { setDialogProps: (props: DialogProps) => void } =
    useDialogContext();
  const { setUploading } = useMiscellaneousContext();

  const onUploadTypeChange = (e: any) => {
    setUploadType(e.target.value);
    setFileTypeError(false);
    setImportError(undefined);
  };

  const onHelpClick = (title: string, helpContent: any) => {
    setDialogProps({
      open: true,
      onClose: () => {
        setDialogProps(initialDialogProps);
      },
      heading: title,
      children: helpContent,
      modal: false,
    });
  };

  const onDrop = useCallback(
    (accepted: any[], rejected: any[]) => {
      let anyRejected = fileTypeError;
      if (accepted.length) {
        setZipFiles([...zipFiles, ...accepted]);
        anyRejected = false;
      }
      if (rejected.length) {
        anyRejected = true;
      }
      setFileTypeError(anyRejected);
      setImportError(undefined);
    },
    [zipFiles, fileTypeError]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFileTypes,
    onDrop,
  });

  const clearFiles = () => {
    setZipFiles([]);
    setShpFileNames([]);
    setFileTypeError(false);
    setImportError(undefined);
  };

  const renderUploadErrors = (errors: string[]) => {
    if (uploadType === uploadTypes.detailed) {
      return (
        <>
          <span>
            Please ensure that the following rule(s) are satisfied in the
            uploaded shapefiles:
          </span>
          <DetailedUploadIssuesList style={{ margin: 0 }}>
            {errors.map((x: string, index: number) => (
              <DetailedUploadIssuesListItem key={index}>
                {x}
              </DetailedUploadIssuesListItem>
            ))}
          </DetailedUploadIssuesList>
        </>
      );
    }

    if (errors.length === 1) {
      return <span>{`${errors[0]} in the uploaded shapefiles`}</span>;
    }
    return (
      <>
        <span>The following errors occurred in the uploaded shapefiles:</span>
        <SimpleUploadIssuesList style={{ margin: 0 }}>
          {errors.map((x: string, index: number) => (
            <li key={index}>{x}</li>
          ))}
        </SimpleUploadIssuesList>
      </>
    );
  };

  const addFeatures = (uploadResults: any) => {
    uploadResults.patches.forEach((x: any) => {
      dispatch({
        type: "addPatch",
        payload: x,
      });
    });
    uploadResults.trees.forEach((x: any) => {
      dispatch({
        type: "addTree",
        payload: x,
      });
    });
    uploadResults.revegetationAreas.forEach((x: any) => {
      dispatch({
        type: "addRevegetationArea",
        payload: x,
      });
    });
  };

  const handleRequestError = (error: string) => {
    setImportError(
      <div>
        There was a GP service error: {error}
        <br />
        Please contact NVR Map support at{" "}
        <LinkComponent
          href={`mailto:${nvrEmailAddress}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {nvrEmailAddress}
        </LinkComponent>
      </div>
    );
  };

  const handleCriticalValidationFailure = (errors: any) => {
    const Errors = renderUploadErrors(errors);
    setImportError(Errors);
  };

  const handleNonCriticalValidationFailure = (errors: any) => {
    const children = errors.map((x: string) => {
      return { content: x, type: "info" };
    });
    setDialogProps({
      open: true,
      onClose: () => setDialogProps(initialDialogProps),
      heading: children.length === 1 ? "Warning" : "Warnings",
      children: <DialogCallout calloutConfigs={children}></DialogCallout>,
    });
  };

  const importFiles = () => {
    setImporting(true);
    setFileTypeError(false);
    setImportError(undefined);
    setShpFileNames([]);
    let geojsonPromises: Promise<any>[] = [];
    zipFiles.forEach((file) => {
      geojsonPromises.push(
        validateZipFileContents(file)
          .then(() => readAndConvertShapefile(file))
          .then((result) =>
            checkNoAdminFields(result, user, nvrToolMode, uploadType)
          )
      );
    });

    Promise.all(geojsonPromises).then(
      async (results) => {
        const geojsonFcs = results.map((x) => x.geojson);
        const shpFileNames = results.map((x) => `${x.shpFileName}.shp`);

        setShpFileNames(shpFileNames);
        setZipFiles([]);

        let uploadResults: UploadResults;
        if (uploadType === uploadTypes.simple) {
          uploadResults = await getSimpleUploadResults(
            geojsonFcs,
            map,
            nvrToolMode
          );
        } else {
          uploadResults = await getDetailedUploadResults(
            geojsonFcs,
            map,
            nvrToolMode
          );
        }
        if (uploadResults.errors?.requestError) {
          handleRequestError(uploadResults.errors?.requestError);
        } else if (uploadResults.errors?.criticalErrors?.length) {
          handleCriticalValidationFailure(uploadResults.errors.criticalErrors);
        } else {
          if (uploadResults.errors?.nonCriticalErrors?.length) {
            handleNonCriticalValidationFailure(
              uploadResults.errors.nonCriticalErrors
            );
          }
          setUploading(true);
          addFeatures(uploadResults);
        }
        setImporting(false);
      },
      (error) => {
        setZipFiles([]);
        setShpFileNames([]);
        setImportError(<span style={{ whiteSpace: "pre-line" }}>{error}</span>);
        setImporting(false);
      }
    );
  };

  useEffect(() => {
    const featuresExist = Object.keys(state.features).length !== 0;
    setExistingFeatures(featuresExist);
    if (!featuresExist) {
      setExistingFeaturesWarning(false);
    }
  }, [state.features]);

  useEffect(() => {
    setExistingFeaturesWarning(Object.keys(state.features).length !== 0);
    // eslint-disable-next-line
  }, []);

  if (existingFeatures) {
    return (
      <Container>
        {existingFeaturesWarning && (
          <Callout type="info">
            Existing features will need to be cleared before a new import
          </Callout>
        )}
      </Container>
    );
  }

  return (
    <Container>
      <UploadTypeContainer>
        <Dropdown
          value={uploadType}
          onChange={onUploadTypeChange}
          disabled={uploadTypeFixed}
          style={{ width: "100%" }}
        >
          {Object.values(uploadTypes).map((x) => (
            <option key={x} value={x}>
              {x} Upload
            </option>
          ))}
        </Dropdown>
      </UploadTypeContainer>
      <DropzoneContainer>
        <StyledDropZone {...getRootProps({ className: "dropzone" })}>
          <input {...getInputProps()} />
          <p style={{ display: "inline" }}>
            Drag and drop any number of zipped shapefiles or click to select
          </p>
          <HelpButtonContainer>
            <HelpButton
              onClick={(e: any) => {
                onHelpClick(
                  "How to upload data",
                  <HowToUpload mode={nvrToolMode} uploadType={uploadType} />
                );
                e.stopPropagation();
              }}
            />
          </HelpButtonContainer>
        </StyledDropZone>
      </DropzoneContainer>
      <FileListContainer>
        {zipFiles.map((x: any, index: number) => (
          <FileContainer key={index}>
            <TickIcon style={tickStyle} />
            {x.name}
          </FileContainer>
        ))}
      </FileListContainer>
      {fileTypeError && (
        <FileErrorContainer>
          <Callout type="error">Only .zip files are supported</Callout>
        </FileErrorContainer>
      )}
      {!!zipFiles.length && (
        <ActionsContainer>
          <FilledButton disabled={!zipFiles.length} onClick={importFiles}>
            Import
          </FilledButton>
          {!!zipFiles.length && (
            <FilledButtonAlt
              style={{ marginRight: "8px" }}
              onClick={clearFiles}
            >
              Clear
            </FilledButtonAlt>
          )}
        </ActionsContainer>
      )}
      {importing && (
        <MessageContainer>
          <ProgressMessage message="Importing" />
        </MessageContainer>
      )}
      {!!importError && (
        <ImportErrorContainer>
          <Callout type="error">{importError}</Callout>
        </ImportErrorContainer>
      )}
    </Container>
  );
}

export default UploadWidget;
